import { Factory, WifiOff } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { AdminManufacturerFleetHealth } from "@/gen/api"

interface ManufacturerFleetHealthWidgetProps {
  manufacturerHealth?: AdminManufacturerFleetHealth[]
}

function safeNumber(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value))
}

export function ManufacturerFleetHealthWidget({
  manufacturerHealth,
}: ManufacturerFleetHealthWidgetProps) {
  const rows = (manufacturerHealth || [])
    .map((item) => {
      const totalCount = safeNumber(item.totalCount)
      const onlineCount = safeNumber(item.onlineCount)
      const offlineCount = safeNumber(item.offlineCount)
      const healthPercentage =
        typeof item.healthPercentage === "number"
          ? item.healthPercentage
          : totalCount > 0
            ? (onlineCount * 100) / totalCount
            : 0

      return {
        id: item.manufacturerId || item.manufacturerName || "unknown",
        name: item.manufacturerName || "Unknown",
        totalCount,
        onlineCount,
        offlineCount,
        healthPercentage: clampPercent(healthPercentage),
      }
    })
    .filter((item) => item.totalCount > 0)
    .sort((a, b) => a.healthPercentage - b.healthPercentage || b.totalCount - a.totalCount)

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Factory className="size-5" />
            Fleet Health by Manufacturer
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Online and offline devices by manufacturer
        </p>
      </CardHeader>
      <CardContent>
        {rows.length > 0 ? (
          <div className="max-h-[360px] space-y-4 overflow-y-auto pr-1">
            {rows.map((item) => {
              const onlineWidth = item.totalCount
                ? clampPercent((item.onlineCount / item.totalCount) * 100)
                : 0
              const offlineWidth = item.totalCount
                ? clampPercent((item.offlineCount / item.totalCount) * 100)
                : 0

              return (
                <div key={item.id} className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.totalCount} total | {item.onlineCount} online / {item.offlineCount}{" "}
                        offline
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold">
                      {Math.round(item.healthPercentage)}%
                    </span>
                  </div>
                  <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-chart-2" style={{ width: `${onlineWidth}%` }} />
                    <div className="h-full bg-destructive" style={{ width: `${offlineWidth}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <WifiOff className="mb-3 size-12 opacity-30" />
            <p className="text-sm">No manufacturer data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
